// TransactionList component displays a list of transactions with filtering and modal details.
//
// Usage:
//   - Admin: <TransactionList /> (shows full date filter bar: today, week, month, all)
//   - POS/Cashier: <TransactionList restrictToToday={true} /> (shows only today's transactions, no date filter bar)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../services/axiosInstance";
import EmptyState from "../CommonComponents/EmptyState";
import FilterBar from "../CommonComponents/FilterBar";
import { ListOrdered, Grid, List } from "lucide-react";
import TransactionCard from "./TransactionCard";
import Modal from "../CommonComponents/TransactionModal";
import { fetchBranches } from "../../services/branchApi";
import { fetchUsers } from "../../services/usersApi";

const APP_TIMEZONE = "Asia/Manila";

const formatDateOnlyUtc = (date) => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatDateInTimeZone = (date, timeZone = APP_TIMEZONE) => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    const year = parts.find((p) => p.type === "year")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    return `${year}-${month}-${day}`;
};

const shiftDateOnlyByDaysUtc = (dateOnly, days) => {
    const d = new Date(`${dateOnly}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() - days);
    return formatDateOnlyUtc(d);
};

const formatDisplayDate = (dateOnly) => {
    if (!dateOnly) return "-";
    const date = new Date(`${dateOnly}T00:00:00`);
    if (Number.isNaN(date.getTime())) return dateOnly;
    return date.toLocaleDateString();
};

const StockBadge = ({ value, tone, title, prefix = "" }) => {
    const toneClasses = {
        beginning: "bg-sky-50 text-sky-700 ring-sky-200",
        added: "bg-emerald-50 text-emerald-700 ring-emerald-200",
        ending: "bg-indigo-50 text-indigo-700 ring-indigo-200",
        sold: "bg-amber-50 text-amber-700 ring-amber-200",
    };

    return (
        <span
            className={`inline-flex min-w-[3rem] items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums ring-1 ${toneClasses[tone] || "bg-gray-50 text-gray-700 ring-gray-200"}`}
            title={title}
        >
            {prefix}{Number(value || 0).toLocaleString()}
        </span>
    );
};

const getManilaDateRange = ({ dateFilter, customStartDate, customEndDate }) => {
    if (dateFilter === "custom") {
        return {
            startDate: customStartDate || "",
            endDate: customEndDate || "",
        };
    }

    if (dateFilter === "all") {
        return { startDate: "", endDate: "" };
    }

    const now = new Date();
    const todayManila = formatDateInTimeZone(now, APP_TIMEZONE);

    if (dateFilter === "today") {
        return { startDate: todayManila, endDate: todayManila };
    }

    if (dateFilter === "week") {
        const weekdayName = new Intl.DateTimeFormat("en-US", { timeZone: APP_TIMEZONE, weekday: "long" }).format(now);
        const weekdayIndex = {
            Sunday: 0,
            Monday: 1,
            Tuesday: 2,
            Wednesday: 3,
            Thursday: 4,
            Friday: 5,
            Saturday: 6,
        }[weekdayName] ?? 1;
        const daysSinceMonday = (weekdayIndex + 6) % 7;
        const start = shiftDateOnlyByDaysUtc(todayManila, daysSinceMonday);
        return { startDate: start, endDate: todayManila };
    }

    if (dateFilter === "month") {
        const [year, month] = todayManila.split("-");
        const start = `${year}-${month}-01`;
        return { startDate: start, endDate: todayManila };
    }

    return { startDate: "", endDate: "" };
};

function TransactionList({ onSelect, restrictToToday = false, showSummary = true, useExpensesPagination = true }) {
    // Get logged-in user info from Redux
    const user = useSelector(state => state.auth.user);
    const customer_id = user?.customer_id;
    const branch_id = user?.branch_id;
    const user_id = user?.id;
    // All useState declarations at the top
    const [transactions, setTransactions] = useState([]);
    const [stockMovements, setStockMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState(() => (restrictToToday ? "grid" : "summary"));
    const [dateFilter, setDateFilter] = useState("today");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [cashierFilter, setCashierFilter] = useState("");
    const [selectedTx, setSelectedTx] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [branches, setBranches] = useState([]);
    const [users, setUsers] = useState([]);
    const [branchFilter, setBranchFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalExpenses, setTotalExpenses] = useState(0);
    const listScrollRef = useRef(null);
    const pageSize = 20;
    const filteredCashierUsers = useMemo(() => {
        if (!branchFilter) return users || [];
        return (users || []).filter((u) => {
            const userBranchId = u.branch_id ?? u.branch?.id ?? "";
            return String(userBranchId) === String(branchFilter);
        });
    }, [users, branchFilter]);

    // Fetch transactions on mount and when filters change
    useEffect(() => {
        if (dateFilter === "custom" && (!customStartDate || !customEndDate)) {
            setTransactions([]);
            setStockMovements([]);
            setError(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        console.log('[TransactionList] Filters:', { branchFilter, dateFilter });
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");

        // Build params with filters - Best Practice: Server-side filtering
        let params = {};

        if (restrictToToday && customer_id && branch_id) {
            // Cashier view: fixed branch and today only
            params = { customer_id, branch_id };
            if (user_id) params.user_id = user_id;
        } else {
            // Admin view: flexible filtering
            if (customer_id) params.customer_id = customer_id;
            if (branchFilter) params.branch_id = Number(branchFilter);
            if (cashierFilter) params.user_id = Number(cashierFilter);
        }

        // Add status filter (both admin and cashier)
        if (statusFilter) params.status = statusFilter;

        // Add date filter (admin and cashier)
        if (dateFilter) {
            params.dateFilter = dateFilter; // 'today', 'week', 'month', 'all', 'custom'
            if (dateFilter === "custom") {
                if (customStartDate) params.startDate = customStartDate;
                if (customEndDate) params.endDate = customEndDate;
            }
        }

        axiosInstance.get("admin/orders", {
            params,
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                console.log('[TransactionList] API response:', res.data);
                const transactionList = Array.isArray(res.data) ? res.data : res.data?.orders || [];
                const movementList = Array.isArray(res.data?.stockMovements) ? res.data.stockMovements : [];
                setTransactions(transactionList);
                setStockMovements(movementList);
                setError(null);
            })
            .catch(() => {
                setError("Failed to load transactions");
            })
            .finally(() => setLoading(false));
    }, [restrictToToday, customer_id, branch_id, user_id, branchFilter, cashierFilter, statusFilter, dateFilter, customStartDate, customEndDate]);

    useEffect(() => {
        if (!customer_id) {
            setTotalExpenses(0);
            return;
        }

        if (dateFilter === "custom" && (!customStartDate || !customEndDate)) {
            setTotalExpenses(0);
            return;
        }

        const { startDate, endDate } = getManilaDateRange({ dateFilter, customStartDate, customEndDate });
        const params = {};

        if (restrictToToday && branch_id) {
            params.branch_id = branch_id;
        } else {
            if (branchFilter) params.branch_id = branchFilter;
            if (cashierFilter) params.user_id = cashierFilter;
        }

        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        axiosInstance.get("/pos/expenses", { params })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const total = list.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);
                setTotalExpenses(total);
            })
            .catch(() => {
                setTotalExpenses(0);
            });
    }, [restrictToToday, customer_id, branch_id, branchFilter, cashierFilter, dateFilter, customStartDate, customEndDate]);

    useEffect(() => {
        if (dateFilter !== "custom") {
            setCustomStartDate("");
            setCustomEndDate("");
        }
    }, [dateFilter]);

    useEffect(() => {
        if (!cashierFilter) return;
        const cashierStillVisible = filteredCashierUsers.some((u) => String(u.id) === String(cashierFilter));
        if (!cashierStillVisible) {
            setCashierFilter("");
        }
    }, [cashierFilter, filteredCashierUsers]);

    // Fetch branches and users for filters
    useEffect(() => {
        if (!customer_id) return;
        fetchBranches(customer_id)
            .then(res => {
                setBranches(res.data.data);
            })
            .catch(() => {
                setBranches([]);
            });

        fetchUsers()
            .then(res => {
                setUsers(res.data.data || []);
            })
            .catch(() => {
                setUsers([]);
            });
    }, [customer_id]);

    // Helper for relative time
    function getRelativeTime(dateString) {
        const now = new Date();
        const date = new Date(dateString);
        const diff = Math.floor((now - date) / 1000); // seconds
        if (diff < 60) return 'Just Now';
        if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
        return date.toLocaleDateString();
    }

    // Server-side filtering is handled by backend API - no client-side filtering needed
    // This follows best practice for performance and scalability
    const filtered = restrictToToday && user_id
        ? transactions.filter(tx => String(tx.user_id || tx.staff_id || tx.cashier_id) === String(user_id))
        : transactions;

    useEffect(() => {
        setCurrentPage(1);
    }, [restrictToToday, customer_id, branch_id, user_id, branchFilter, cashierFilter, statusFilter, dateFilter, customStartDate, customEndDate, viewMode]);

    const productSummary = useMemo(() => {
        const summaryMap = new Map();
        const scopeMap = new Map();

        filtered.forEach((tx) => {
            const products = Array.isArray(tx.products) ? tx.products : [];
            const soldDate = formatDateInTimeZone(new Date(tx.created_at), APP_TIMEZONE);
            products.forEach((item) => {
                const qty = Number(item.qty || 0);
                const price = Number(item.price || 0);
                if (!qty) return;

                const name = item.name || "Unknown Item";
                const productKey = String(item.product_id || name);
                const key = `${soldDate}-${productKey}`;
                const existing = summaryMap.get(key) || {
                    key,
                    productKey,
                    date: soldDate,
                    name,
                    price,
                    prices: new Set(),
                    totalQty: 0,
                    stockIn: 0,
                    totalAmount: 0,
                    beginningStock: 0,
                    endingStock: 0,
                    snapshotBalances: new Map(),
                };

                existing.prices.add(price);
                if (!existing.price) existing.price = price;
                existing.totalQty += qty;
                existing.totalAmount += qty * price;
                summaryMap.set(key, existing);

                const branchId = item.branch_id || tx.branch_id || "unknown";
                const branchSnapshotKey = `${branchId}-${item.product_id || name}`;
                const stockBefore = item.stock_before;
                const stockAfter = item.stock_after;
                const hasStockSnapshot = stockBefore !== null
                    && stockBefore !== undefined
                    && stockAfter !== null
                    && stockAfter !== undefined;

                if (hasStockSnapshot) {
                    const timestamp = new Date(tx.created_at).getTime() || 0;
                    const branchSnapshot = existing.snapshotBalances.get(branchSnapshotKey) || {
                        firstTimestamp: timestamp,
                        lastTimestamp: timestamp,
                        stockBefore: Number(stockBefore),
                        stockAfter: Number(stockAfter),
                        stockInBeforeFirstSale: 0,
                        stockInAfterLastSale: 0,
                    };

                    if (timestamp <= branchSnapshot.firstTimestamp) {
                        branchSnapshot.firstTimestamp = timestamp;
                        branchSnapshot.stockBefore = Number(stockBefore);
                    }

                    if (timestamp >= branchSnapshot.lastTimestamp) {
                        branchSnapshot.lastTimestamp = timestamp;
                        branchSnapshot.stockAfter = Number(stockAfter);
                    }

                    existing.snapshotBalances.set(branchSnapshotKey, branchSnapshot);
                }

                if (!scopeMap.has(productKey)) {
                    scopeMap.set(productKey, {
                        branchStocks: new Map(),
                        groups: new Map(),
                    });
                }

                const scope = scopeMap.get(productKey);
                scope.groups.set(key, existing);

                const branchProductKey = `${branchId}-${item.product_id || name}`;
                if (!scope.branchStocks.has(branchProductKey)) {
                    scope.branchStocks.set(branchProductKey, Number(item.current_stock || 0));
                }
            });
        });

        stockMovements.forEach((movement) => {
            const qty = Number(movement.qty || 0);
            if (!qty) return;

            const stockInDate = movement.date;
            const productKey = String(movement.product_id || movement.name || "Unknown Item");
            const key = `${stockInDate}-${productKey}`;
            const existing = summaryMap.get(key);
            if (!existing) return;

            const movementTimestamp = new Date(movement.timestamp).getTime() || 0;
            const branchProductKey = `${movement.branch_id || "unknown"}-${movement.product_id || movement.name || "Unknown Item"}`;
            const branchSnapshot = existing.snapshotBalances.get(branchProductKey);

            if (existing.snapshotBalances.size > 0 && !branchSnapshot) return;

            if (branchSnapshot) {
                if (movementTimestamp <= branchSnapshot.firstTimestamp) {
                    branchSnapshot.stockInBeforeFirstSale += qty;
                } else if (movementTimestamp > branchSnapshot.lastTimestamp) {
                    branchSnapshot.stockInAfterLastSale += qty;
                }

                existing.snapshotBalances.set(branchProductKey, branchSnapshot);
            }

            existing.stockIn += qty;
            summaryMap.set(key, existing);
        });

        scopeMap.forEach((scope) => {
            let runningEndingStock = Array.from(scope.branchStocks.values())
                .reduce((sum, stock) => sum + Number(stock || 0), 0);

            const groups = Array.from(scope.groups.values())
                .sort((a, b) => b.date.localeCompare(a.date));

            groups.forEach((group) => {
                if (group.snapshotBalances.size > 0) {
                    const snapshots = Array.from(group.snapshotBalances.values());
                    group.beginningStock = snapshots.reduce((sum, snapshot) => {
                        return sum + Number(snapshot.stockBefore || 0) - Number(snapshot.stockInBeforeFirstSale || 0);
                    }, 0);
                    group.endingStock = snapshots.reduce((sum, snapshot) => {
                        return sum + Number(snapshot.stockAfter || 0) + Number(snapshot.stockInAfterLastSale || 0);
                    }, 0);
                    runningEndingStock = group.beginningStock;
                    return;
                }

                group.endingStock = runningEndingStock;
                group.beginningStock = runningEndingStock + group.totalQty - Number(group.stockIn || 0);
                runningEndingStock = group.beginningStock;
            });
        });

        return Array.from(summaryMap.values()).map(({ snapshotBalances, prices, ...item }) => ({
            ...item,
            priceLabel: prices?.size > 1 ? "Mixed" : item.price,
        })).sort((a, b) => {
            if (b.date !== a.date) return b.date.localeCompare(a.date);
            if (b.totalQty !== a.totalQty) return b.totalQty - a.totalQty;
            return b.totalAmount - a.totalAmount;
        });
    }, [filtered, stockMovements]);

    const activeItems = viewMode === "summary" ? productSummary : filtered;
    const totalPages = Math.max(1, Math.ceil(activeItems.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const pagedTransactions = filtered.slice(startIndex, startIndex + pageSize);
    const pagedProductSummary = productSummary.slice(startIndex, startIndex + pageSize);
    const totalSales = filtered.reduce((sum, tx) => sum + Number(tx.total || 0), 0);
    const totalItemsSold = productSummary.reduce((sum, item) => sum + item.totalQty, 0);
    const listPaddingBottom = "pb-4";
    const scrollSpacingBottom = activeItems.length > 0 ? "pb-2" : "pb-0";
    const handlePageChange = (page) => {
        const nextPage = Math.min(totalPages, Math.max(1, page));
        if (nextPage === safePage) return;

        setCurrentPage(nextPage);
        window.requestAnimationFrame(() => {
            listScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            listScrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    // Prepare filter configurations for FilterBar
    const filterConfigs = [
        {
            type: 'react-select',
            label: 'Branch',
            value: (() => {
                if (!branchFilter) return { value: "", label: "All Branches" };
                const found = (branches || []).find(b => String(b.id) === branchFilter);
                return found ? { value: String(found.id), label: found.name } : { value: branchFilter, label: branchFilter };
            })(),
            onChange: setBranchFilter,
            options: [
                { value: "", label: "All Branches" },
                ...((Array.isArray(branches) ? branches : []).map(branch => ({ value: String(branch.id), label: branch.name })))
            ],
            isSearchable: true,
            placeholder: "All Branches",
            minWidth: '180px',
            hide: restrictToToday, // Hide for cashier view
        },
        {
            type: 'react-select',
            label: 'Date',
            value: (() => {
                const labelMap = {
                    all: "All",
                    today: "Today",
                    week: "This Week",
                    month: "This Month",
                    custom: "Custom",
                };
                const value = dateFilter || "all";
                return { value, label: labelMap[value] || value };
            })(),
            onChange: setDateFilter,
            options: [
                { value: "all", label: "All" },
                { value: "today", label: "Today" },
                { value: "week", label: "This Week" },
                { value: "month", label: "This Month" },
                { value: "custom", label: "Custom" },
            ],
            isSearchable: true,
            placeholder: "All",
            minWidth: '140px',
            hide: false,
        },
        {
            type: 'date',
            label: 'Start Date',
            value: customStartDate,
            onChange: setCustomStartDate,
            minWidth: '170px',
            hide: dateFilter !== "custom",
            max: customEndDate || undefined,
        },
        {
            type: 'date',
            label: 'End Date',
            value: customEndDate,
            onChange: setCustomEndDate,
            minWidth: '170px',
            hide: dateFilter !== "custom",
            min: customStartDate || undefined,
        },
        {
            type: 'react-select',
            label: 'Cashier',
            value: (() => {
                if (!cashierFilter) return { value: "", label: "All Cashiers" };
                const found = filteredCashierUsers.find(u => String(u.id) === cashierFilter)
                    || (users || []).find(u => String(u.id) === cashierFilter);
                return found ? { value: String(found.id), label: found.username || found.email } : { value: cashierFilter, label: cashierFilter };
            })(),
            onChange: setCashierFilter,
            options: [
                { value: "", label: "All Cashiers" },
                ...(filteredCashierUsers.map(user => ({
                    value: String(user.id),
                    label: user.username || user.email,
                })))
            ],
            isSearchable: true,
            placeholder: "All Cashiers",
            minWidth: '180px',
            hide: restrictToToday, // Hide for cashier view
        },
        {
            type: 'react-select',
            label: 'Status',
            value: (() => {
                if (!statusFilter) return { value: "", label: "All Status" };
                const labelMap = {
                    PAID: "Paid",
                    Pending: "Pending",
                    Cancelled: "Cancelled",
                };
                return { value: statusFilter, label: labelMap[statusFilter] || statusFilter };
            })(),
            onChange: setStatusFilter,
            options: [
                { value: "", label: "All Status" },
                { value: "PAID", label: "Paid" },
                { value: "Pending", label: "Pending" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            isSearchable: true,
            placeholder: "All Status",
            minWidth: '140px',
            className: 'ml-2',
        },
    ];

    const gridToggleButton = (
        <button
            onClick={() => setViewMode("grid")}
            className={`p-2 rounded-xl ${viewMode === "grid" ? "bg-black text-white" : "bg-[#e5e5ea] text-gray-700"} transition-colors`}
            aria-label="Cards view"
        >
            <Grid size={18} />
        </button>
    );

    const summaryToggleButton = (
        <button
            onClick={() => setViewMode("summary")}
            className={`p-2 rounded-xl ${viewMode === "summary" ? "bg-black text-white" : "bg-[#e5e5ea] text-gray-700"} transition-colors`}
            aria-label="Product sales summary"
        >
            <List size={18} />
        </button>
    );

    // View toggle buttons. Admin defaults to summary and shows Cards as the second icon.
    const viewToggleActions = restrictToToday ? (
        <>
            {gridToggleButton}
            {summaryToggleButton}
        </>
    ) : (
        <>
            {summaryToggleButton}
            {gridToggleButton}
        </>
    );

    return (
        <div className="flex flex-col h-full min-h-0">
            {/* Minimalist Apple-inspired Filter Bar - Always visible */}
            <FilterBar filters={filterConfigs} extraActions={viewToggleActions} />

            {/* Loading State */}
            {loading && (
                <div className="flex-1 flex items-center justify-center">
                    <div>Loading transactions...</div>
                </div>
            )}

            {/* Error State */}
            {error && !loading && (
                <div className="flex-1 flex items-center justify-center">
                    <EmptyState
                        icon={ListOrdered}
                        title="Failed to Load Transactions"
                        message="Check your internet connection and try again."
                        onRetry={() => window.location.reload()}
                        retryLabel="Reload"
                    />
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && activeItems.length === 0 && (
                <div className="flex-1 flex items-center justify-center">
                    <EmptyState
                        icon={ListOrdered}
                        title={viewMode === "summary" ? "No Products Sold" : "No Transactions"}
                        message={viewMode === "summary" ? "No sold items match the selected filters." : "No transaction data available for the selected period."}
                    />
                </div>
            )}

            {/* Transaction List */}
            {!loading && !error && activeItems.length > 0 && (
                <div ref={listScrollRef} className={`flex-1 min-h-0 overflow-y-auto mt-2 ${scrollSpacingBottom}`}>
                    {viewMode === "summary" ? (
                        <div className={`overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm ${listPaddingBottom}`}>
                            <div className="min-w-[1060px]">
                                <div className="grid grid-cols-[110px,minmax(160px,1.7fr),130px,130px,120px,110px,100px,120px] gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-[11px] font-semibold uppercase text-gray-500">
                                    <div>Date</div>
                                    <div>Item Name</div>
                                    <div className="text-right">Beginning Stock</div>
                                    <div className="text-right">Added Stock</div>
                                    <div className="text-right">Count Sold</div>
                                    <div className="text-right">Ending Stock</div>
                                    <div className="text-right">Unit Price</div>
                                    <div className="text-right">Sales Total</div>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {pagedProductSummary.map((item) => {
                                        const unitPriceText = item.priceLabel === "Mixed"
                                            ? "Mixed"
                                            : `₱${Number(item.priceLabel || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                                        return (
                                            <div
                                                key={item.key}
                                                className="grid grid-cols-[110px,minmax(160px,1.7fr),130px,130px,120px,110px,100px,120px] items-center gap-3 px-4 py-3 text-sm text-gray-700"
                                            >
                                                <div className="whitespace-nowrap text-gray-600">{formatDisplayDate(item.date)}</div>
                                                <div className="min-w-0 truncate font-medium text-gray-900" title={item.name}>{item.name}</div>
                                                <div className="flex justify-end">
                                                    <StockBadge value={item.beginningStock} tone="beginning" title="Beginning stock" />
                                                </div>
                                                <div className="flex justify-end">
                                                    <StockBadge value={item.stockIn} tone="added" title="Additional stocks" prefix="+" />
                                                </div>
                                                <div className="flex justify-end">
                                                    <StockBadge value={item.totalQty} tone="sold" title="Count sold" prefix="-" />
                                                </div>
                                                <div className="flex justify-end">
                                                    <StockBadge value={item.endingStock} tone="ending" title="Ending stock" prefix="= " />
                                                </div>
                                                <div className="text-right tabular-nums">
                                                    {unitPriceText}
                                                </div>
                                                <div className="text-right tabular-nums">
                                                    <div className="text-[11px] font-medium text-gray-400">
                                                        {item.totalQty.toLocaleString()} x {unitPriceText}
                                                    </div>
                                                    <div className="font-semibold text-gray-900">₱{item.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${listPaddingBottom}`}>
                            {pagedTransactions.map(tx => (
                                <TransactionCard
                                    key={tx.id}
                                    tx={tx}
                                    getRelativeTime={getRelativeTime}
                                    onClick={() => {
                                        setSelectedTx(tx);
                                        setModalOpen(true);
                                        if (onSelect) onSelect(tx.id);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!loading && !error && filtered.length > 0 && showSummary && (
                <div className="mt-4 rounded-xl bg-white p-3 shadow-md ring-1 ring-gray-100">
                    <div className="grid grid-cols-3 gap-1.5 text-[10px] text-gray-700 sm:gap-2 sm:text-xs">
                        <div className="rounded-lg bg-gray-50 px-2 py-1.5 sm:px-3 sm:py-2">
                            <div className="text-[9px] font-medium uppercase tracking-tight text-gray-500 sm:text-[10px] sm:tracking-wide">{viewMode === "summary" ? "Items Sold" : "Total Count"}</div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900 sm:text-sm">{viewMode === "summary" ? totalItemsSold.toLocaleString() : filtered.length}</div>
                        </div>
                        <div className="rounded-lg bg-gray-50 px-2 py-1.5 sm:px-3 sm:py-2">
                            <div className="text-[9px] font-medium uppercase tracking-tight text-gray-500 sm:text-[10px] sm:tracking-wide">Total Sales</div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900 sm:text-sm">₱{totalSales.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="rounded-lg bg-gray-50 px-2 py-1.5 sm:px-3 sm:py-2">
                            <div className="text-[9px] font-medium uppercase tracking-tight text-gray-500 sm:text-[10px] sm:tracking-wide">Total Expenses</div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900 sm:text-sm">₱{totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                    </div>
                </div>
            )}

            {!loading && !error && activeItems.length > 0 && useExpensesPagination && (
                <div className="mt-4 flex items-center justify-between gap-3 overflow-x-auto whitespace-nowrap">
                    <div className="shrink-0 text-xs text-gray-500">
                        Showing {startIndex + 1}-{Math.min(startIndex + pageSize, activeItems.length)} of {activeItems.length}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                            onClick={() => handlePageChange(safePage - 1)}
                            disabled={safePage === 1}
                        >
                            Prev
                        </button>
                        <div className="text-xs text-gray-600">
                            Page {safePage} of {totalPages}
                        </div>
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                            onClick={() => handlePageChange(safePage + 1)}
                            disabled={safePage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                {selectedTx ? (
                    <div>
                        <h2 className="font-bold text-lg mb-2">Order #{selectedTx.order_number}</h2>
                        <div className="mb-1 text-sm text-gray-600">Date: {new Date(selectedTx.created_at).toLocaleDateString()} {new Date(selectedTx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                        <div className="mb-1 text-sm">Status: <span className="font-semibold">{selectedTx.status}</span></div>
                        <div className="mb-1 text-sm">Payment: <span className="font-semibold">{selectedTx.payment_method}</span></div>
                        <div className="mb-1 text-sm">Seat Number: <span className="font-semibold">{selectedTx.seat_number || '-'}</span></div>
                        <div className="mb-1 text-sm">Cashier: <span className="font-semibold">{selectedTx.cashier_name || selectedTx.cashier || selectedTx.user_name || '-'}</span></div>
                        <div className="mb-1 text-sm">Items: <span className="font-semibold">{selectedTx.products ? selectedTx.products.reduce((sum, item) => sum + (item.qty || 0), 0) : 0}</span></div>
                        {selectedTx.products && selectedTx.products.length > 0 && (
                            <ul className="list-disc list-inside text-xs text-gray-700 max-h-32 overflow-y-auto mb-2">
                                {selectedTx.products.map((item, idx) => (
                                    <li key={idx}>{item.name || "Item"} <span className="text-gray-400">x{item.qty || 1}</span></li>
                                ))}
                            </ul>
                        )}
                        <div className="mt-2 text-right">
                            <span className="text-xs text-gray-500 block">Total Amount Paid:</span>
                            <span className="text-lg font-bold text-grey">₱{Number(selectedTx.total).toLocaleString()}</span>
                        </div>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
}

export default TransactionList;
